import {
  Vulnerability,
  PaginationInfo,
  VulnerabilityStats,
  VulnerabilityBannerResponse,
  RemediationSummary,
  SentinelOneAsset,
  WindowsOSUpdate,
  RemediationItemProgress,
} from '../Utils/types';

const getVulnerabilityRowKey = (
  item: {
    softwareAssetInventoryId?: string;
    cveId?: string;
    packageIdentifier?: string;
    version?: string;
    title?: string;
    cveUrl?: string;
  },
  source: string,
  page: number,
  index: number,
) => {
  const compositeKey = [source, item.softwareAssetInventoryId, item.cveId, item.packageIdentifier, item.version, item.title, item.cveUrl]
    .filter(Boolean)
    .join('::');

  return compositeKey || `${source}::page-${page}::row-${index}`;
};

const getAgeInDays = (publishedDateValue?: string) => {
  if (!publishedDateValue) return 0;
  const publishedDate = new Date(publishedDateValue);
  if (Number.isNaN(publishedDate.getTime())) return 0;
  const today = new Date();
  return Math.floor((today.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24));
};

const toDisplaySeverity = (severity?: string) => {
  if (!severity) return 'Unknown';
  const normalized = severity.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const buildSummaryReasons = (reasonCounts: Record<string, number>, includeRequestFailure: boolean) => {
  const reasons: string[] = [];

  if (reasonCounts.missingAgentId > 0) {
    reasons.push(`Missing agentId: ${reasonCounts.missingAgentId}`);
  }
  if (reasonCounts.missingFixVersion > 0) {
    reasons.push(`Missing latest version: ${reasonCounts.missingFixVersion}`);
  }
  if (reasonCounts.missingCurrentVersion > 0) {
    reasons.push(`Missing current version: ${reasonCounts.missingCurrentVersion}`);
  }
  if (reasonCounts.alreadyLatestVersion > 0) {
    reasons.push(`Already latest version: ${reasonCounts.alreadyLatestVersion}`);
  }
  if (reasonCounts.missingPackageIdentifier > 0) {
    reasons.push(`Missing packageIdentifier: ${reasonCounts.missingPackageIdentifier}`);
  }
  if (includeRequestFailure) {
    reasons.push('Create remediation plan request failed');
  }

  return reasons;
};

export async function getSentinelOneBanner(
  selectedTenantId: string,
  getSentinelOneBanner: (tenantId: string) => Promise<VulnerabilityBannerResponse>,
) {
  const response = await getSentinelOneBanner(selectedTenantId);
  return response;
}

export async function getSentinelOneVulnerabilities(params: {
  selectedTenantId: string;
  page: number;
  itemsPerPage: number;
  selectedSeverity: string;
  selectedKev: string;
  selectedEpss: string;
  searchTerm: string;
  getSentinelOneSoftwareVulnerabilities: (tenantId: string, page: number, pageSize: number) => Promise<any>;
}) {
  const response = await params.getSentinelOneSoftwareVulnerabilities(params.selectedTenantId, params.page, params.itemsPerPage);
  const sentinelItems = Array.isArray(response) ? response : response?.data;
  const sentinelPagination = !Array.isArray(response) ? response?.pagination : null;

  if (Array.isArray(sentinelItems)) {
    const mappedAllData: Vulnerability[] = sentinelItems.map((item: any, index: number) => ({
      rowKey: getVulnerabilityRowKey(
        {
          softwareAssetInventoryId: item.softwareAssetInventoryId,
          cveId: item.cveId,
          packageIdentifier: item.packageIdentifier,
          version: item.version,
          title: item.name || 'Unknown',
          cveUrl: item.cveUrl,
        },
        'SentinelOne',
        params.page,
        index,
      ),
      softwareAssetInventoryId: item.softwareAssetInventoryId || `${item.cveId || 'unknown'}-${item.name || 'asset'}-${index}`,
      cveId: item.cveId || 'N/A',
      cveUrl: item.cveUrl,
      title: item.name || 'Unknown',
      severity: toDisplaySeverity(item.baseSeverity),
      cvssScore: item.cvss ? parseFloat(item.cvss) : 0,
      epssScore: item.epssScore ?? 0,
      kev: item.isKEV ?? item.kev ?? item.isKev ?? item.knownExploited ?? item.knownExploitedVulnerability ?? null,
      affectedDevicesCount: Number(item.installedEndpoints || 0) + Number(item.installedServers || 0),
      ageInDays: getAgeInDays(item.cveDatePublished),
      description: item.descriptions,
      version: item.version,
      packageIdentifier: item.packageIdentifier,
      includedAgentIds: Array.isArray(item.includedAgentIds)
        ? item.includedAgentIds.filter((agentId: unknown): agentId is string => typeof agentId === 'string' && !!agentId)
        : [],
      fixVersion: item.fixVersion,
      includedAssets: item.includedAssets,
      updateType: item.updateType,
      kbId: item.kbId,
    }));

    let filteredData = mappedAllData;

    if (params.selectedSeverity) {
      filteredData = filteredData.filter((item) => item.severity.toLowerCase() === params.selectedSeverity.toLowerCase());
    }

    if (params.selectedEpss === '>0.5') {
      filteredData = filteredData.filter((item) => item.epssScore > 0.5);
    }
    if (params.selectedEpss === '>0.7') {
      filteredData = filteredData.filter((item) => item.epssScore > 0.7);
    }

    if (params.searchTerm) {
      const normalizedSearch = params.searchTerm.toLowerCase();
      filteredData = filteredData.filter((item) => {
        return [item.cveId, item.title, item.version, item.packageIdentifier]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));
      });
    }

    const hasServerPagination =
      !!sentinelPagination ||
      response?.totalPages !== undefined ||
      response?.totalRecords !== undefined ||
      response?.pageNumber !== undefined ||
      response?.pageSize !== undefined;

    const rawTotalRecords = Number(sentinelPagination?.totalRecords ?? response?.totalRecords);
    const totalRecords = Number.isFinite(rawTotalRecords) && rawTotalRecords > 0 ? rawTotalRecords : filteredData.length;
    const rawTotalPages = Number(sentinelPagination?.totalPages ?? response?.totalPages);
    const calculatedTotalPages =
      Number.isFinite(rawTotalPages) && rawTotalPages > 0 ? rawTotalPages : Math.max(1, Math.ceil(totalRecords / params.itemsPerPage));
    const rawPageNumber = Number(sentinelPagination?.pageNumber ?? response?.pageNumber ?? params.page);
    const pageNumber = Number.isFinite(rawPageNumber) && rawPageNumber > 0 ? rawPageNumber : params.page;
    const rawPageSize = Number(sentinelPagination?.pageSize ?? response?.pageSize ?? params.itemsPerPage);
    const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0 ? rawPageSize : params.itemsPerPage;

    let effectivePageNumber = pageNumber;

    if (hasServerPagination) {
      return {
        vulnerabilities: filteredData,
        paginationInfo: {
          pageNumber: effectivePageNumber,
          pageSize,
          totalRecords,
          totalPages: calculatedTotalPages,
          hasPreviousPage: effectivePageNumber > 1,
          hasNextPage: effectivePageNumber < calculatedTotalPages,
        },
      };
    } else {
      const boundedPage = Math.min(params.page, calculatedTotalPages);
      effectivePageNumber = boundedPage;
      const start = (boundedPage - 1) * params.itemsPerPage;
      const end = boundedPage * params.itemsPerPage;
      return {
        vulnerabilities: filteredData.slice(start, end),
        paginationInfo: {
          pageNumber: effectivePageNumber,
          pageSize,
          totalRecords,
          totalPages: calculatedTotalPages,
          hasPreviousPage: effectivePageNumber > 1,
          hasNextPage: effectivePageNumber < calculatedTotalPages,
        },
      };
    }
  } else {
    return {
      vulnerabilities: [],
      paginationInfo: null,
      stats: {
        totalVulnerabilities: 0,
        totalKEV: 0,
        totalCritical: 0,
        totalEPSSAboveThreshold: 0,
        affectedDevices: 0,
      },
    };
  }
}

// One remediation plan per selected vulnerability - each plan only targets the devices that
// vulnerability's own response reported (includedAgentIds/includedAssets), instead of one
// combined plan spanning every selected row's devices.
export async function handleSentinelOneRemediation(params: {
  selectedVulnerabilities: Record<string, Vulnerability>;
  selectedTenantId: string;
  addToast: (message: string, type: string, duration: number, title?: string) => void;
  getManagedPackageByIdentifier: (identifier: string) => Promise<any>;
  createRemediationPlan: (params: any) => Promise<any>;
  onProgress?: (update: RemediationItemProgress) => void;
}) {
  const vulnerabilities = Object.values(params.selectedVulnerabilities);
  const selectedCount = vulnerabilities.length;
  try {
    params.addToast(`Processing ${selectedCount} vulnerabilit${selectedCount > 1 ? 'ies' : 'y'} for remediation...`, 'info', 3000);

    const reasonCounts = {
      missingAgentId: 0,
      missingFixVersion: 0,
      missingCurrentVersion: 0,
      alreadyLatestVersion: 0,
      missingPackageIdentifier: 0,
    };
    let successfullyCreated = 0;
    let requestFailedCount = 0;

    for (const vulnerability of vulnerabilities) {
      const key = vulnerability.rowKey;
      const title = vulnerability.title || vulnerability.cveId || 'Unknown vulnerability';
      const cveId = vulnerability.cveId;
      const updateType = vulnerability.updateType || 'App';

      params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Validating target devices…' });

      const includedAgentIdsFromResponse = (vulnerability.includedAgentIds || []).filter(
        (agentId): agentId is string => typeof agentId === 'string' && !!agentId,
      );
      const agentIdsFromAssets = (vulnerability.includedAssets || [])
        .map((asset: SentinelOneAsset) => asset.agentId)
        .filter((agentId): agentId is string => !!agentId);
      const agentIds = Array.from(new Set([...includedAgentIdsFromResponse, ...agentIdsFromAssets]));

      if (agentIds.length === 0) {
        reasonCounts.missingAgentId += 1;
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'No devices found' });
        continue;
      }

      // Handle OS updates - each selected KB/category becomes its own plan targeting only the
      // devices this vulnerability's response listed.
      if (updateType === 'OS') {
        const kbId = vulnerability.kbId?.trim();
        const fixVersion = vulnerability.fixVersion?.trim();

        if (!kbId || !fixVersion) {
          reasonCounts.missingFixVersion += 1;
          params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Missing KB/fix version' });
          continue;
        }

        const windowsOSUpdateList: WindowsOSUpdate[] = [
          {
            category: fixVersion,
            kbIds: [kbId],
            selectUpdatesWithoutReboot: false,
            operationType: 'Install',
          },
        ];

        const remediationPlanData = {
          PlanName: `SentinelOne Vulnerability Remediation - ${kbId} - ${new Date().toLocaleDateString()}`,
          IsEmergencyRemediationPlan: false,
          SendEmailOnCompletion: false,
          RemediationPlanStartTime: new Date().toISOString(),
          IsActive: true,
        };

        params.onProgress?.({
          key,
          title,
          cveId,
          stage: 'processing',
          message: `Creating plan for ${agentIds.length} device${agentIds.length !== 1 ? 's' : ''}…`,
        });

        try {
          const response = await params.createRemediationPlan({
            remediationPlanPatchList: [],
            windowsOSUpdateList,
            targetDeviceIds: agentIds,
            remediationPlanData,
          });

          if (response) {
            successfullyCreated += 1;
            params.onProgress?.({ key, title, cveId, stage: 'success', message: 'Remediation plan created' });
          } else {
            requestFailedCount += 1;
            params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Request failed' });
          }
        } catch (error) {
          console.error(`Error creating remediation plan for KB ${kbId}:`, error);
          requestFailedCount += 1;
          params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Unexpected error' });
        }
        continue;
      }

      // Handle App updates
      const packageIdentifier = vulnerability.packageIdentifier?.trim() || '';
      const currentVersion = vulnerability.version?.trim() || '';

      let isEligible = true;

      if (!packageIdentifier) {
        reasonCounts.missingPackageIdentifier += 1;
        isEligible = false;
      }

      if (!currentVersion) {
        reasonCounts.missingCurrentVersion += 1;
        isEligible = false;
      }

      if (!isEligible) {
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Missing package/version info' });
        continue;
      }

      params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Checking latest package version…' });

      let latestVersion = '';
      try {
        const packageResponse = await params.getManagedPackageByIdentifier(packageIdentifier);
        if (packageResponse?.isSuccess && packageResponse?.data?.packageVersion) {
          latestVersion = String(packageResponse.data.packageVersion).trim();
        }
      } catch (error) {
        console.error(`Error fetching latest version for ${packageIdentifier}:`, error);
      }

      if (!latestVersion) {
        reasonCounts.missingFixVersion += 1;
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Missing latest version' });
        continue;
      }

      if (latestVersion === currentVersion) {
        reasonCounts.alreadyLatestVersion += 1;
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Already on latest version' });
        continue;
      }

      const remediationPlanData = {
        PlanName: `SentinelOne Vulnerability Remediation - ${packageIdentifier} - ${new Date().toLocaleDateString()}`,
        IsEmergencyRemediationPlan: false,
        SendEmailOnCompletion: false,
        RemediationPlanStartTime: new Date().toISOString(),
        IsActive: true,
      };

      params.onProgress?.({
        key,
        title,
        cveId,
        stage: 'processing',
        message: `Creating plan for ${agentIds.length} device${agentIds.length !== 1 ? 's' : ''}…`,
      });

      try {
        const response = await params.createRemediationPlan({
          remediationPlanPatchList: [{ packageIdentifier, targetVersion: latestVersion }],
          windowsOSUpdateList: [],
          targetDeviceIds: agentIds,
          remediationPlanData,
        });

        if (response) {
          successfullyCreated += 1;
          params.onProgress?.({ key, title, cveId, stage: 'success', message: 'Remediation plan created' });
        } else {
          requestFailedCount += 1;
          params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Request failed' });
        }
      } catch (error) {
        console.error(`Error creating remediation plan for ${packageIdentifier}:`, error);
        requestFailedCount += 1;
        params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Unexpected error' });
      }
    }

    const summary: RemediationSummary = {
      totalRequested: selectedCount,
      successfullyCreated,
      failedOrNotEligible: selectedCount - successfullyCreated,
      reasons: buildSummaryReasons(reasonCounts, requestFailedCount > 0),
    };

    const successPercentage = selectedCount > 0 ? Math.round((successfullyCreated / selectedCount) * 100) : 0;
    let toastMessage = '';

    if (successfullyCreated === selectedCount) {
      toastMessage = `✓ All ${selectedCount} remediation plan${selectedCount !== 1 ? 's' : ''} created successfully!`;
    } else if (successfullyCreated > 0) {
      toastMessage = `${successfullyCreated}/${selectedCount} remediation plan${selectedCount !== 1 ? 's' : ''} created (${successPercentage}% success)`;
      if (summary.reasons.length > 0) {
        toastMessage += ` • ${summary.reasons.join(' • ')}`;
      }
    } else {
      toastMessage = `Failed to create remediation plans. ${summary.reasons.length > 0 ? summary.reasons.join(' • ') : 'Please try again.'}`;
    }

    params.addToast(toastMessage, successfullyCreated > 0 ? 'success' : 'warning', 6000, 'SentinelOne Remediation Summary');

    return { remediationSummary: summary, clearSelection: successfullyCreated > 0 };
  } catch (error) {
    console.error('Error creating SentinelOne remediation plan:', error);
    vulnerabilities.forEach((vulnerability) => {
      params.onProgress?.({
        key: vulnerability.rowKey,
        title: vulnerability.title || vulnerability.cveId || 'Unknown vulnerability',
        cveId: vulnerability.cveId,
        stage: 'failed',
        message: 'Unexpected error',
      });
    });
    const summary: RemediationSummary = {
      totalRequested: selectedCount,
      successfullyCreated: 0,
      failedOrNotEligible: selectedCount,
      reasons: ['Create remediation plan request failed'],
    };
    params.addToast('Failed to create remediation plan', 'error', 4000);
    return { remediationSummary: summary, clearSelection: false };
  }
}
